import React from 'react';
import PageShell from '../../components/PageShell';
import { Eyebrow, SectionHeading, InfoCard, GradientButton, CTABanner } from '../../components/common/Primitives';
import { Check, X as XIcon } from 'lucide-react';

const COLORS = [
  { name: 'Deep navy', hex: '#08123F', bg: 'bg-navy' },
  { name: 'Purple',    hex: '#6F2CE8', bg: 'bg-purple' },
  { name: 'Royal blue',hex: '#2563EB', bg: 'bg-royal' },
  { name: 'Cyan',      hex: '#0BB8D4', bg: 'bg-cyan' },
  { name: 'Mint',      hex: '#10C89A', bg: 'bg-mint' },
  { name: 'Canvas',    hex: '#F7F8FF', bg: 'bg-canvas-100 border border-canvas-200' },
];

export default function LogoAssets() {
  return (
    <PageShell>
      <section className="px-6 pt-12 pb-8">
        <div className="mx-auto max-w-[1240px]">
          <Eyebrow color="purple">MEDIA RESOURCE</Eyebrow>
          <div className="mt-3 grid grid-cols-1 gap-10 lg:grid-cols-[1.2fr_1fr]">
            <div>
              <h1 className="text-[42px] md:text-[52px] font-extrabold leading-[1.06] tracking-tight text-navy">Logo and visual assets</h1>
              <p className="mt-5 max-w-xl text-[15.5px] leading-relaxed text-ink-500">Approved brand marks, product visuals and usage guidance for media, institutional and partner communication.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <GradientButton>Download Brand Assets</GradientButton>
                <GradientButton variant="outline">Request Additional Assets</GradientButton>
              </div>
            </div>
            <div className="rounded-3xl bg-gradient-to-br from-purple-50 via-white to-mint-50 border border-canvas-200 grid place-items-center text-purple text-[12.5px] font-bold tracking-widest aspect-[4/3]">GYAN DHARA<br/>BRAND ASSET<br/>LIBRARY</div>
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="LOGO VARIANTS" title="Approved identity files" desc="Use supplied vector or transparent-background files. Do not redraw, recolour or distort the logo." />
          <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-3">
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="aspect-[16/10] rounded-xl bg-brand-logo grid place-items-center text-white text-[12px] font-bold">GYAN DHARA</div>
              <div className="mt-4 text-[15px] font-bold text-navy">Primary logo</div>
              <p className="mt-1 text-[12.5px] text-ink-500">For white and light backgrounds</p>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-navy p-6 shadow-soft">
              <div className="aspect-[16/10] rounded-xl bg-brand-logo grid place-items-center text-white text-[12px] font-bold">GYAN DHARA</div>
              <div className="mt-4 text-[15px] font-bold text-white">Reverse logo</div>
              <p className="mt-1 text-[12.5px] text-white/70">For dark or photographic backgrounds</p>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-purple-50 p-6 shadow-soft">
              <div className="aspect-[16/10] rounded-xl bg-brand-logo grid place-items-center text-white text-[12px] font-bold">GYAN DHARA</div>
              <div className="mt-4 text-[15px] font-bold text-purple">Symbol mark</div>
              <p className="mt-1 text-[12.5px] text-ink-500">For compact applications and favicons</p>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 py-16 bg-white">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="COLOUR SYSTEM" title="Primary brand palette" desc="Use deep navy for institutional trust, purple and blue for immersive technology, and cyan-mint accents for exploration and progress." />
          <div className="mt-8 grid grid-cols-3 gap-5 md:grid-cols-6">
            {COLORS.map((c,i)=>(<div key={i}><div className={`h-24 rounded-2xl ${c.bg}`} /><div className="mt-3 text-[13px] font-bold text-navy">{c.name}</div><div className="text-[12px] text-ink-500">{c.hex}</div></div>))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16">
        <div className="mx-auto max-w-[1240px]">
          <SectionHeading eyebrow="USAGE GUIDANCE" title="Protect clarity and recognition" desc="Keep adequate clear space and use approved variants for the background." />
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="text-[13.5px] font-bold text-mint">DO</div>
              <ul className="mt-3 space-y-2">{['Use approved SVG or transparent PNG files','Maintain the original proportions','Use the reverse logo on dark backgrounds','Keep clear space around the mark'].map((s,i)=>(<li key={i} className="flex items-start gap-2 text-[13px] text-ink-700"><Check className="mt-0.5 h-4 w-4 text-mint" />{s}</li>))}</ul>
            </div>
            <div className="rounded-2xl border border-canvas-200 bg-white p-6 shadow-soft">
              <div className="text-[13.5px] font-bold text-red-500">DO NOT</div>
              <ul className="mt-3 space-y-2">{['Stretch, rotate or compress the logo','Change the gradient or brand colours','Place the logo over a busy background','Add unapproved shadows, outlines or effects'].map((s,i)=>(<li key={i} className="flex items-start gap-2 text-[13px] text-ink-700"><XIcon className="mt-0.5 h-4 w-4 text-red-500" />{s}</li>))}</ul>
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-24">
        <div className="mx-auto max-w-[1240px]">
          <CTABanner title="Use only approved and current Gyan Dhara assets." desc="For a publication-specific selection, contact the project team before use." primary={{ label: 'Download Brand Assets', to: '#' }} />
        </div>
      </section>
    </PageShell>
  );
}
